import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Card, message, Typography, Select, Flex, Space, Modal } from 'antd';
import { loadState } from '../../stores/store';
import {
  searchApplications,
  deleteApplication,
  updateApplicationRound,
  createApplication,
  getApplicationsByContestant
} from '../../api/application';
import { searchContestants, updateContestantProvince } from '../../api/contestant';
import { getSoloEvents } from '../../api/event';
import { fetchProvinces } from '../../api/competition';
import ApplicationTable from '../../components/Applications/applicationsTable';
import GroupMembersModal from '../../components/Applications/groupMembersModal';
import ApplyEventsModal from '../../components/Applications/applyEventsModal';
import { useSettings } from '../../contexts/settingsContext';
import './applications.scss';

const { Title } = Typography;
const { Option } = Select;

const Applications = () => {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [initialSelectedEvents, setInitialSelectedEvents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isGroupMembersModalVisible, setIsGroupMembersModalVisible] = useState(false);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const { state: settings } = useSettings();
  const [selectedRound, setSelectedRound] = useState(settings.selectedRound);
  const [isApplyEventsModalVisible, setIsApplyEventsModalVisible] = useState(false);
  const [contestant, setContestant] = useState();
  const [isApplying, setIsApplying] = useState(false);

  // Province selection state
  const [provinces, setProvinces] = useState([]);
  const [activeProvinces, setActiveProvinces] = useState([]);
  const [provinceSelectionModalVisible, setProvinceSelectionModalVisible] = useState(false);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);
  const [updatingProvince, setUpdatingProvince] = useState(false);

  const state = useMemo(() => loadState(), []);
  const userRole = state.user.role;
  const showRoundSelector = userRole === 'admin' || settings.selectedRound > 1;

  const contestantHasProvince = useMemo(
    () => !!(contestant && contestant.province),
    [contestant]
  );

  const selectableProvinces = useMemo(() => {
    if (userRole === 'admin') {
      return provinces || [];
    }
    return activeProvinces || [];
  }, [userRole, provinces, activeProvinces]);

  const loadProvinces = useCallback(async () => {
    try {
      const competitionId =
        settings?.selectedCompetition?._id || settings?.selectedCompetitionId;

      if (!competitionId) {
        setProvinces([]);
        setActiveProvinces([]);
        return;
      }

      const data = await fetchProvinces(competitionId);
      setProvinces(data || []);

      const now = new Date();
      const active = (data || []).filter(prov => {
        if (!prov.date) return true;
        const d = new Date(prov.date);
        return d >= now;
      });

      setActiveProvinces(active);
    } catch (error) {
      console.error('Error loading audition dates', error);
      setProvinces([]);
      setActiveProvinces([]);
    }
  }, [settings]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      let fetchedApplications = [];

      if (userRole === 'contestant') {
        const contestantsResponse = await searchContestants();
        const contestants = contestantsResponse?.data || [];

        const contestantObj = contestants[0];
        setContestant(contestantObj);

        if (contestantObj) {
          const idVal = contestantObj?.id_number;
          const passportVal = contestantObj?.passport_number;

          const hasValidIdNumber =
            idVal &&
            typeof idVal === 'string' &&
            idVal.trim() !== '' &&
            idVal.trim() !== '0' &&
            idVal.trim().toLowerCase() !== 'no';

          const idToSend = hasValidIdNumber ? idVal : passportVal;

          fetchedApplications = await getApplicationsByContestant(
            contestantObj._id,
            idToSend
          );
        } else {
          fetchedApplications = [];
        }
      } else if (userRole === 'guardian') {
        const contestantsResponse = await searchContestants({ guardian: state.user._id });
        const contestants = contestantsResponse?.data || [];
        setContestant(contestants[0]);

        const contestantIds = contestants.map(c => c._id);
        fetchedApplications = await searchApplications({
          contestant: { $in: contestantIds }
        });
      } else if (userRole === 'admin') {
        let allApps = await searchApplications();
        fetchedApplications = allApps.filter(
          application => application.province === state.user.activeProvince
        );
      } else {
        fetchedApplications = await searchApplications({
          createdBy: state.user._id
        });
      }

      setApplications(fetchedApplications);
    } catch (err) {
      console.error('Error fetching applications', err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [userRole, state.user._id, state.user.activeProvince]);

  const fetchEvents = useCallback(async () => {
    try {
      const eventsData = await getSoloEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events', error);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    loadProvinces();
  }, [loadProvinces]);

  const filteredApplications = useMemo(() => {
    if (!contestant) return applications;
    return applications.filter(app => app.round === contestant.round);
  }, [applications, contestant]);

  const closeGroupMembersModal = useCallback(() => {
    setIsGroupMembersModalVisible(false);
  }, []);

  const handleDelete = useCallback(
    async applicationId => {
      try {
        await deleteApplication(applicationId);
        message.success('Application deleted successfully');
        fetchApplications();
      } catch (error) {
        message.error('Failed to delete application');
        console.error('Error deleting application:', error);
      }
    },
    [fetchApplications]
  );

  const handleMoveToNextRound = useCallback(
    async applicationId => {
      try {
        await updateApplicationRound(applicationId);
        message.success('Application moved to next round');
        fetchApplications();
      } catch (error) {
        message.error('Failed to move application to next round');
        console.error('Error moving application to next round:', error);
      }
    },
    [fetchApplications]
  );

  const handleApplicationUpdate = () => {
    fetchApplications();
  };

  const handleRoundChange = useCallback(round => {
    setSelectedRound(round);
  }, []);

  const handleSelectEvent = useCallback(event => {
    setSelectedEvents(prevSelectedEvents => {
      const isSelected = prevSelectedEvents.some(e => e._id === event._id);
      if (isSelected) {
        return prevSelectedEvents.filter(e => e._id !== event._id);
      } else {
        return [...prevSelectedEvents, event];
      }
    });
  }, []);

  const closeApplyEventsModal = useCallback(() => {
    setIsApplyEventsModalVisible(false);
  }, []);

  const handleApplyToEvents = useCallback(
    async () => {
      if (!contestant) {
        message.error('No contestant found');
        return;
      }

      if (!contestant.province) {
        message.warning('Please select an audition date first');
        setProvinceSelectionModalVisible(true);
        return;
      }

      setIsApplying(true);

      const soloApplications = applications.filter(app => app.contestant && !app.group);
      const appliedEventIds = soloApplications
        .filter(app => app.round === contestant.round)
        .map(app => app.event._id);

      const applicationsToCreate = selectedEvents.filter(
        event => !appliedEventIds.includes(event._id)
      );
      const applicationsToDelete = initialSelectedEvents.filter(
        event => !selectedEvents.some(e => e._id === event._id)
      );

      try {
        const createPromises = applicationsToCreate.map(event => {
          const applicationData = {
            event: event._id,
            contestant: contestant._id,
            round: contestant.round,
            createdBy: state.user._id,
            province: state.user.activeProvince,
            instrument: event.instrument || undefined
          };
          return createApplication(applicationData);
        });

        const deletePromises = soloApplications
          .filter(app => applicationsToDelete.some(event => event._id === app.event._id))
          .map(app => deleteApplication(app._id));

        await Promise.all([...createPromises, ...deletePromises]);

        message.success('Applications updated successfully');
        fetchApplications();
      } catch (error) {
        message.error('Failed to update applications');
        console.error('Error updating applications:', error);
      } finally {
        setIsApplying(false);
        closeApplyEventsModal();
      }
    },
    [
      selectedEvents,
      initialSelectedEvents,
      applications,
      selectedRound,
      fetchApplications,
      contestant,
      state.user._id,
      state.user.activeProvince,
      closeApplyEventsModal
    ]
  );

  const isAdmin = useMemo(() => userRole === 'admin', [userRole]);

  const handleOpenProvinceSelectionModal = useCallback(() => {
    if (!contestant) return;
    const existingProvince = contestant.province;
    const existingProvinceId =
      existingProvince && typeof existingProvince === 'object'
        ? existingProvince._id
        : existingProvince;
    setSelectedProvinceId(existingProvinceId || null);
    setProvinceSelectionModalVisible(true);
  }, [contestant]);

  const handleProvinceSelectionCancel = useCallback(() => {
    setProvinceSelectionModalVisible(false);
    setSelectedProvinceId(null);
  }, []);

  const handleProvinceSelectionConfirm = useCallback(async () => {
    if (!contestant || !selectedProvinceId) {
      message.warning('Please select an audition date first');
      return;
    }

    try {
      setUpdatingProvince(true);
      await updateContestantProvince(contestant._id, selectedProvinceId);

      setContestant(prev =>
        prev
          ? {
              ...prev,
              province:
                prev.province && typeof prev.province === 'object'
                  ? { ...prev.province, _id: selectedProvinceId }
                  : selectedProvinceId
            }
          : prev
      );

      message.success("Contestant's audition date updated");
      setProvinceSelectionModalVisible(false);
      setSelectedProvinceId(null);
      fetchApplications();
    } catch (error) {
      console.error('Error updating contestant audition date', error);
      message.error('Failed to update contestant audition date');
    } finally {
      setUpdatingProvince(false);
    }
  }, [contestant, selectedProvinceId, fetchApplications]);

  const openApplyEventsModal = useCallback(() => {
    if (!contestant) {
      message.error('No contestant found');
      return;
    }

    if (!contestant.province) {
      message.warning('Please select an audition date first');
      handleOpenProvinceSelectionModal();
      return;
    }

    const soloApplications = applications.filter(app => app.contestant && !app.group);
    const appliedEvents = soloApplications
      .filter(app => app.round === contestant.round)
      .map(app => app.event);
    setSelectedEvents(appliedEvents);
    setInitialSelectedEvents(appliedEvents);
    setIsApplyEventsModalVisible(true);
  }, [applications, contestant, handleOpenProvinceSelectionModal]);

  const roundOptions = useMemo(() => {
    if (userRole === 'admin') {
      return [1, 2, 3];
    } else {
      return [1, 2, 3].filter(round => round <= settings.selectedRound);
    }
  }, [userRole, settings.selectedRound]);

  return (
    <div>
      <Flex
        style={{ marginBottom: '15px' }}
        className="dashboard_page_header_area"
        align="center"
        justify="space-between"
      >
        <Title className="dashboard_page_title" level={4} style={{ margin: 0 }}>
          Applications
        </Title>
        <Space className="dashboard_page_title_actions">
          {showRoundSelector && (
            <Select
              placeholder="Select Round"
              onChange={handleRoundChange}
              value={contestant?.round || selectedRound}
              style={{ width: 200 }}
            >
              {roundOptions.map(round => (
                <Option key={round} value={round}>
                  Round {round}
                </Option>
              ))}
            </Select>
          )}
          {userRole === 'contestant' && settings.nationalApplicationsOpen && (
            contestantHasProvince ? (
              <Button type="primary" onClick={openApplyEventsModal}>
                Apply to Events
              </Button>
            ) : (
              <Button type="primary" onClick={handleOpenProvinceSelectionModal}>
                Select audition date
              </Button>
            )
          )}
        </Space>
      </Flex>
      <Card bordered={false}>
        {contestant && !contestantHasProvince ? (
          <div>
            <p>
              Before viewing or applying for events, please select your audition date.
            </p>
            <Button type="primary" onClick={handleOpenProvinceSelectionModal}>
              Select audition date
            </Button>
          </div>
        ) : (
          <ApplicationTable
            loading={loading}
            applications={filteredApplications}
            isAdmin={isAdmin}
            onDelete={handleDelete}
            onMoveToNextRound={handleMoveToNextRound}
            onApplicationUpdate={handleApplicationUpdate}
          />
        )}
      </Card>
      <GroupMembersModal
        visible={isGroupMembersModalVisible}
        onClose={closeGroupMembersModal}
        groupMembers={selectedGroupMembers}
        groupName={selectedApplication?.group?.group_name}
      />
      <ApplyEventsModal
        visible={isApplyEventsModalVisible}
        onClose={closeApplyEventsModal}
        events={events}
        loading={loading}
        userRole={userRole}
        onSelectEvent={handleSelectEvent}
        selectedEvents={selectedEvents}
        onApply={handleApplyToEvents}
        confirmLoading={isApplying}
        contestant={contestant}
        applications={applications}
      />
      <Modal
        title="Select Province"
        visible={provinceSelectionModalVisible}
        onOk={handleProvinceSelectionConfirm}
        confirmLoading={updatingProvince}
        onCancel={handleProvinceSelectionCancel}
        destroyOnClose
      >
        <p>
          Please select an audition date for{' '}
          {contestant ? `${contestant.first_name} ${contestant.last_name}` : 'this contestant'}.
        </p>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a province"
          value={selectedProvinceId || undefined}
          onChange={value => setSelectedProvinceId(value)}
          showSearch
          optionFilterProp="children"
        >
          {selectableProvinces &&
            Array.isArray(selectableProvinces) &&
            selectableProvinces.map(prov => (
              <Option key={prov._id} value={prov._id}>
                {prov.province} ({prov.location}) - {prov.date}
              </Option>
            ))}
        </Select>
      </Modal>
    </div>
  );
};

export default Applications;
